import { DeviceRegistry } from '../audio/deviceRegistry';
import { pairOutputForInput } from '../audio/devicePolicy';
import { OutputDiagnostics, PlatformQuirks } from '../audio/platformQuirks';
import { LoggerBridge } from '../audio/shared';

// Plays each call's remote audio through its own hidden <audio autoplay playsinline> element. On desktop
// every element is routed to the output that belongs to the microphone in use, so a headset's mic brings
// its own speaker. On mobile the OS owns routing (Android cannot pick an output at all) and the elements
// play to the current route.
export class OutputRouter implements OutputDiagnostics {
    private readonly elements = new Set<HTMLAudioElement>();
    private currentSinkId: string | null = null;
    private warnedUnsupported = false;

    constructor(
        private readonly logger: LoggerBridge,
        private readonly registry: DeviceRegistry,
        private readonly quirks: PlatformQuirks) { }

    get sinkId(): string | null {
        return this.currentSinkId;
    }

    get state(): string | null {
        if (this.elements.size === 0) return null;
        return [...this.elements].some(element => !element.paused) ? 'playing' : 'paused';
    }

    createElement(): HTMLAudioElement {
        const element = document.createElement('audio');
        element.autoplay = true;
        element.setAttribute('playsinline', '');
        element.style.display = 'none';
        document.body.appendChild(element);
        this.elements.add(element);
        return element;
    }

    releaseElement(element: HTMLAudioElement): void {
        this.elements.delete(element);
        try { element.pause(); } catch { }
        element.srcObject = null;
        element.remove();
    }

    // Pairs the output with the microphone and points every element at it. `explicit` marks a deliberate
    // mic choice, so the pairing follows that mic rather than an OS-default headset (see pairOutputForInput).
    async route(micId: string | null, explicit = false): Promise<boolean> {
        try {
            if (!this.quirks.allowsOutputSelection) {
                await this.logger.info('Output routing is OS-managed on this platform; skipping speaker selection', { micId });
                return true;
            }
            const snapshot = await this.registry.enumerate();
            if (snapshot.outputs.length === 0) {
                await this.logger.warning('No audiooutput devices found during auto-select');
                return false;
            }
            const { mic, output } = pairOutputForInput(micId, snapshot, { explicit });
            if (!mic) {
                await this.logger.warning('Could not resolve mic device for auto-select', { micId });
                return false;
            }
            const ok = await this.applyToAll(output?.deviceId ?? null);
            if (ok) await this.logger.info('Auto-selected speaker for mic', { mic, speaker: output, explicit });
            return ok;
        } catch (e) {
            await this.logger.error('Failed to auto-select speaker for mic', String(e), { micId });
            return false;
        }
    }

    // The user picked an output themselves. Desktop only; elsewhere the OS route stays.
    async setSink(deviceId: string | null): Promise<boolean> {
        if (!this.quirks.allowsOutputSelection) {
            await this.logger.info('Output selection is OS-managed on this platform; ignoring the requested speaker', { deviceId });
            return false;
        }
        return this.applyToAll(deviceId);
    }

    // A new element joins the current route.
    async applyCurrentSink(element: HTMLAudioElement): Promise<void> {
        if (this.quirks.allowsOutputSelection && this.currentSinkId) {
            await this.applySink(element, this.currentSinkId);
        }
    }

    private async applyToAll(sinkId: string | null): Promise<boolean> {
        this.currentSinkId = sinkId;
        let ok = true;
        for (const element of this.elements) {
            ok = await this.applySink(element, sinkId) && ok;
        }
        return ok;
    }

    private async applySink(element: HTMLAudioElement, sinkId: string | null): Promise<boolean> {
        if (typeof element.setSinkId !== 'function') {
            if (!this.warnedUnsupported) {
                this.warnedUnsupported = true;
                await this.logger.warning('HTMLMediaElement.setSinkId not supported in this browser; system default output will be used', { userAgent: navigator.userAgent });
            }
            return false;
        }
        try {
            await element.setSinkId(sinkId ?? '');
            return true;
        } catch (err: any) {
            if (err?.name === 'NotAllowedError') {
                await this.logger.error('setSinkId denied: speaker-selection permission not granted', String(err), { sinkId });
            } else {
                await this.logger.error('Failed to set speaker output device', String(err), { sinkId });
            }
            return false;
        }
    }
}
